import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import ProteinAnnotationEditorActions from 'molbio/proteins/ProteinAnnotationEditorActions';
import {ANNOTATION_MAX_NOTES_LENGTH, MAX_ANNOTATION_NAME_LENGTH} from 'molbio/proteins/ProteinStore';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import * as keyboard from 'util/Keyboard';

import {ElementKeyboardEvent, InputChangeEvent} from '../../util/Types';

export interface ProteinAnnotationEditorProps {
  proteinId: string;
  name: string;
  type: string;
  notes: string;
  displayStart: string;
  displayEnd: string;
  color: string;
  isAnnotationSelected: boolean;
  // If true, we should set keyboard focus on the editor at the next convenient opportunity and
  // trigger an action that this has been done.
  pendingEditorFocus: boolean;
  isInputValid: boolean;
}

/**
 * The "edit" portion of the protein annotation panel.
 */
export default class ProteinAnnotationEditor extends React.Component<ProteinAnnotationEditorProps> {
  static propTypes = {
    proteinId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    displayStart: PropTypes.string.isRequired,
    displayEnd: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    isAnnotationSelected: PropTypes.bool.isRequired,
    // If true, we should set keyboard focus on the editor at the next convenient opportunity and
    // trigger an action that this has been done.
    pendingEditorFocus: PropTypes.bool.isRequired,
    isInputValid: PropTypes.bool.isRequired,
  };

  componentDidMount(): void {
    this._maybeSetEditorFocus();
  }

  componentDidUpdate(): void {
    this._maybeSetEditorFocus();
  }

  nameInputRef = React.createRef<HTMLInputElement>();

  _maybeSetEditorFocus(): void {
    if (this.props.pendingEditorFocus) {
      // Sometimes we race with other less important focus actions, so setTimeout so we run last.
      setTimeout(() => {
        if (this.nameInputRef.current) {
          this.nameInputRef.current.focus();
          ProteinAnnotationEditorActions.pendingFocusCompleted(this.props.proteinId);
        }
      }, 0);
    }
  }

  trackEditField = (event: string): void => {
    analytics.track('protein', event, {product: ANALYTICS_MODULE.MOLBIO, protein_id: this.props.proteinId});
  };

  _handleSetName = (e: InputChangeEvent): void => {
    ProteinAnnotationEditorActions.setName(this.props.proteinId, e.target.value);
  };

  _handleSetType = (e: InputChangeEvent): void => {
    ProteinAnnotationEditorActions.setType(this.props.proteinId, e.target.value);
  };

  _handleSetNotes = (e: InputChangeEvent): void => {
    ProteinAnnotationEditorActions.setNotes(this.props.proteinId, e.target.value);
  };

  _handleSetDisplayStart = (e: InputChangeEvent): void => {
    ProteinAnnotationEditorActions.setDisplayStart(this.props.proteinId, e.target.value);
  };

  _handleSetDisplayEnd = (e: InputChangeEvent): void => {
    ProteinAnnotationEditorActions.setDisplayEnd(this.props.proteinId, e.target.value);
  };

  _handleSetColor = (color: string): void => {
    ProteinAnnotationEditorActions.setColor(this.props.proteinId, color);
  };

  _handleSaveClick = (): void => {
    this._submitChanges();
  };

  _handleCancelClick = (): void => {
    ProteinAnnotationEditorActions.cancel(this.props.proteinId);
  };

  _handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (keyboard.isEnterKey(e) && this.props.isInputValid) {
      this._submitChanges();
    }
  };

  _submitChanges(): void {
    ProteinAnnotationEditorActions.save(this.props.proteinId);
  }

  render(): JSX.Element {
    const {name, type, notes, displayStart, displayEnd, color, isAnnotationSelected, isInputValid} =
      this.props;
    const titleText = isAnnotationSelected ? 'Edit annotation' : 'New annotation';
    return (
      <div
        onKeyDown={this._handleKeyDown}
        {...getTestProps({component: 'ProteinAnnotationEditor', element: 'root'})}
      >
        <Row>
          <Col md={12}>
            <span className="proteinAnnotationsPanel-editorTitle">{titleText}</span>
          </Col>
        </Row>
        <Row>
          <Col md={6}>Name</Col>
          <Col md={6}>
            <input
              {...getTestProps({
                component: 'ProteinAnnotationEditor',
                element: 'input',
                key: 'annotationName',
              })}
              ref={this.nameInputRef}
              type="text"
              placeholder="Annotation name"
              value={name}
              onChange={this._handleSetName}
              onBlur={() => {
                this.trackEditField('edited_name');
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>Annotation type</Col>
          <Col md={6}>
            <input
              type="text"
              placeholder="Annotation type"
              value={type}
              onChange={this._handleSetType}
              onBlur={() => {
                this.trackEditField('edited_annotation_type');
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>Position</Col>
          <Col md={3}>
            <input
              type="text"
              placeholder="Start"
              value={displayStart}
              onChange={this._handleSetDisplayStart}
            />
          </Col>
          <Col md={3}>
            <input type="text" placeholder="End" value={displayEnd} onChange={this._handleSetDisplayEnd} />
          </Col>
        </Row>
        <Row>
          <Col md={6}>Color</Col>
          <Col md={6}>
            <PopoverColorPicker selectedColor={color} onSelectColor={this._handleSetColor} />
          </Col>
        </Row>
        <Row>
          <Col md={6}>Notes</Col>
          <Col md={6}>
            <input type="text" placeholder="Notes" value={notes} onChange={this._handleSetNotes} />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <button
              className={classNames(
                'button button--primary button--small proteinAnnotationsPanel-editorButton',
                {
                  'is-disabled': !isInputValid,
                }
              )}
              onClick={this._handleSaveClick}
              disabled={!isInputValid}
              type="button"
              {...getTestProps({component: 'ProteinAnnotationEditor', element: 'button', key: 'save'})}
            >
              Save annotation
            </button>
          </Col>
          <Col md={6}>
            <button
              className="button button--tertiaryGray button--small proteinAnnotationsPanel-editorButton"
              onClick={this._handleCancelClick}
              type="button"
            >
              Cancel
            </button>
          </Col>
        </Row>
        {!isInputValid && (
          <Row>
            <span className="proteinAnnotationsPanel-errorMessage">
              {`Please enter a valid name (<= ${MAX_ANNOTATION_NAME_LENGTH} chars.), note (<= ${ANNOTATION_MAX_NOTES_LENGTH} chars.), and position.`}
            </span>
          </Row>
        )}
      </div>
    );
  }
}
